import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FolderKanban, User, CheckSquare, MoreHorizontal, Archive, Lock } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const ProjectCard = ({ project, onEdit, onArchive, isAdmin }) => {
  const navigate = useNavigate()

  const leadName = project.lead?.employee
    ? `${project.lead.employee.first_name} ${project.lead.employee.last_name}`
    : project.lead?.email || null

  const leadInitial = leadName ? leadName.charAt(0).toUpperCase() : '?'

  const handleCardClick = (e) => {
    // Don't navigate if clicking the dropdown
    if (e.target.closest('[data-dropdown]')) return
    navigate(`/projects/${project.id}/board`)
  }

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow border group"
      onClick={handleCardClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Color accent + icon */}
            <div
              className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
              style={{ backgroundColor: project.color + '20', border: `1.5px solid ${project.color}40` }}
            >
              <FolderKanban className="h-4 w-4" style={{ color: project.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-semibold text-sm truncate">{project.name}</h3>
                {project.visibility === 'private' && (
                  <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
              </div>
              {project.status === 'archived' && (
                <Badge variant="secondary" className="text-xs mt-0.5">Archived</Badge>
              )}
            </div>
          </div>

          {isAdmin && (
            <div data-dropdown>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(project) }}>
                    Edit project
                  </DropdownMenuItem>
                  {project.status === 'active' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); onArchive(project) }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        Archive project
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {project.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{project.description}</p>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {leadName ? (
            <div className="flex items-center gap-1.5">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{leadInitial}</AvatarFallback>
              </Avatar>
              <span className="truncate max-w-[120px]">{leadName}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground/60">
              <User className="h-3.5 w-3.5" />
              <span>No lead</span>
            </div>
          )}

          <div className="flex items-center gap-1">
            <CheckSquare className="h-3.5 w-3.5" />
            <span>{project.taskCount ?? 0} tasks</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default ProjectCard
